/**
 * syncSecureStorageUseCase
 * 
 * Business logic for syncing user data between server and local encrypted storage.
 * Uses SecureStorageWebCrypto for persistent encrypted IndexedDB storage.
 * 
 * This UseCase orchestrates the entire sync process:
 * 1. Determines which entity types need syncing
 * 2. Fetches data from Firestore via SyncService
 * 3. Converts DTOs to Storage Entities via remappers
 * 4. Stores encrypted data in IndexedDB via SecureStorageWebCrypto
 * 
 * Matches Flutter's doSync pattern from sync_repository.dart
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { auth } from '@/data/services/firebase';
import { SyncService } from '@/domain/services/sync/syncService';
import {
  SyncEntityType,
  getAllSyncEntityTypes,
  getSyncConfig,
  type UserIndependentSyncEntityType,
} from '@/domain/enums/syncEntityType';
import { type SyncEntity } from '@/domain/entities/sync/syncEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { UserMediaLikeRemapper } from '@/domain/remappers/userMediaLike/userMediaLikeRemapper';
import { CommentLikeRemapper } from '@/domain/remappers/commentLike/commentLikeRemapper';
import { CommunityMessageLikeRemapper } from '@/domain/remappers/communityMessageLike/communityMessageLikeRemapper';
import { SavedContentRemapper } from '@/domain/remappers/savedContent/savedContentRemapper';
import { SavedUserRemapper } from '@/domain/remappers/savedUser/savedUserRemapper';
import { ExclusiveContentSubscriptionRemapper } from '@/domain/remappers/exclusiveContentSubscription/exclusiveContentSubscriptionRemapper';
import { UserSettingRemapper } from '@/domain/remappers/userSetting/userSettingRemapper';
import { UserPremiumPackageRemapper } from '@/domain/remappers/userPremiumPackage/userPremiumPackageRemapper';
import { PremiumPackageRemapper } from '@/domain/remappers/premiumPackage/premiumPackageRemapper';
import { GiftRemapper } from '@/domain/remappers/gift/giftRemapper';
import { RechargeWalletProductRemapper } from '@/domain/remappers/rechargeWalletProduct/rechargeWalletProductRemapper';
import { RoadmapItemLikeRemapper } from '@/domain/remappers/roadmapItemLike/roadmapItemLikeRemapper';
import { RoadmapCommentLikeRemapper } from '@/domain/remappers/roadmapCommentLike/roadmapCommentLikeRemapper';
import { DonationProductRemapper } from '@/domain/remappers/donationProduct/donationProductRemapper';
import { UnlockedContentRemapper } from '@/domain/remappers/unlockedContent/unlockedContentRemapper';
import { WalletRemapper } from '@/domain/remappers/wallet/walletRemapper';
import { BlockedUserRemapper } from '@/domain/remappers/blockedUser/blockedUserRemapper';
import { MutedUserRemapper } from '@/domain/remappers/mutedUser/mutedUserRemapper';
import { UserBlockedMeRemapper } from '@/domain/remappers/userBlockedMe/userBlockedMeRemapper';
import { BlockedConversationRemapper } from '@/domain/remappers/blockedConversation/blockedConversationRemapper';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { getUserPrivateJwkUseCase } from '@/domain/usecases/user/getUserPrivateJwkUseCase';
import { cloudStorageService } from '@/data/storage/remote/cloudStorageService';
import { userService } from '@/domain/services/user/userService';
import { UserUtils } from '@/domain/utils/userUtils';

export class SyncSecureStorageUseCase {
  private secureStorage: SecureStorageWebCrypto;
  private syncService: SyncService;

  constructor(
    storage?: SecureStorageWebCrypto,
    syncService?: SyncService
  ) {
    this.secureStorage = storage || SecureStorageWebCrypto.getInstance();
    this.syncService = syncService || new SyncService();
  }

  /**
   * Execute sync for authenticated user
   * Main entry point - syncs all entity types
   */
  async execute(user: UserEntity): Promise<void> {
    if (!user?.firebaseId) {
      return;
    }

    // console.log('[syncSecureStorageUseCase] Starting sync for user:', user.firebaseId);

    const syncEntityTypes = getAllSyncEntityTypes();

    for (const syncEntityType of syncEntityTypes) {
      try {
        const syncEntity = await this.getSyncEntityStatus(syncEntityType);
        // Sync if sync entity has not been stored yet (done at the end of each successful sync)
        // or if it hasn't completed syncing
        const shouldSync = syncEntity == null || !syncEntity.synced;

        if (!shouldSync) {
          // console.log(`[syncSecureStorageUseCase] Skipping ${syncEntityType} - already synced`);
          continue;
        }

        const config = getSyncConfig(syncEntityType);

        if (config.wait) {
          // Wait for critical syncs (e.g., wallet)
          await this.syncEntityType(syncEntityType, user);
        } else {
          // Fire and forget for non-critical syncs
          this.syncEntityType(syncEntityType, user).catch(error => {
            appLogger.error(`Error syncing ${syncEntityType}`, 'SyncSecureStorageUseCase.execute', { error: error as Error });
          });
        }
      } catch (error) {
        continue; // Continue with next entity type on error
      }
    }

    // console.log('[syncSecureStorageUseCase] Sync initialization complete');
  }

  /**
   * Force a re-sync of a SINGLE entity type, bypassing the already-synced gate.
   *
   * This is the entry point the Firebase Remote Config real-time listener uses to
   * invalidate exactly the cache whose `version*` key bumped. Unlike `execute`, it
   * deliberately does NOT consult `getSyncEntityStatus` — Flutter's listener calls
   * `SyncRepository().syncGifts()` / `.syncRechargeWalletProducts()` /
   * `.syncPremiumPackages()` / `.syncDonationProducts()` directly instead of going
   * through `doSync`'s already-synced gate
   * (lib/services/remote_config_service.dart:214-233, :311-315). The router's own
   * `setEntitySynced` call still runs, which just refreshes `lastSyncTimestamp`.
   *
   * Accepts only `UserIndependentSyncEntityType` — the entity types whose sync
   * path needs no `UserEntity` (`GIFTS`, `RECHARGE_WALLET_PRODUCTS`,
   * `PREMIUM_PACKAGES`, `DONATION_PRODUCTS`, `USER_SETTINGS`; see
   * `domain/enums/syncEntityType.ts`). Passing a `user`-dependent type
   * (`BLOCKED_USERS`, `MUTED_USERS`, `USERS_BLOCKED_ME`,
   * `BLOCKED_CONVERSATIONS`, `UPLOAD_PROFILE_IMAGE`) is a compile error: those
   * need the user entity, which only `execute` has.
   *
   * Returns early (no throw) when there is no authenticated user: every one of the
   * five sync paths goes through `SyncService`, which throws
   * `User not authenticated` without a signed-in user, and a Remote Config push can
   * land while the user is signed out (the listener is app-scoped, not auth-scoped).
   *
   * Errors from the sync itself propagate to the caller (matching Flutter, which
   * awaits the repository call without catching) — callers should handle them.
   */
  async resyncEntity(type: UserIndependentSyncEntityType): Promise<void> {
    if (!auth.currentUser) {
      return;
    }

    await this.syncEntityType(type);
  }

  /**
   * Clear all synced data (on logout)
   */
  async clearSyncedData(): Promise<void> {
    // console.log('[syncSecureStorageUseCase] Clearing all synced data');
    await this.secureStorage.clear();
  }

  /**
   * Get sync status for a specific entity type
   */
  private async getSyncEntityStatus(type: SyncEntityType): Promise<SyncEntity | null> {
    const key = SECURE_STORAGE_KEYS.SYNC_ENTITY + type;
    return await this.secureStorage.getEncrypted<SyncEntity>(key);
  }

  /**
   * Mark entity type as synced
   */
  private async setEntitySynced(type: SyncEntityType): Promise<void> {
    const key = SECURE_STORAGE_KEYS.SYNC_ENTITY + type;
    const syncEntity: SyncEntity = {
      type,
      synced: true,
      lastSyncTimestamp: Date.now(),
    };
    await this.secureStorage.setEncrypted(key, syncEntity);
  }

  /**
   * Guard for the sync branches that read fields off the user entity.
   *
   * `syncEntityType` is reachable both from `execute` (always has the user) and
   * from `resyncEntity` (never does), so `user` is optional there. This narrows
   * it for the `user`-dependent branches and logs instead of dereferencing
   * `undefined` if one is ever routed without a user.
   */
  private hasUser(user: UserEntity | undefined, type: SyncEntityType): user is UserEntity {
    if (!user) {
      appLogger.error(
        `Cannot sync ${type} without a user`,
        'SyncSecureStorageUseCase.syncEntityType'
      );
      return false;
    }

    return true;
  }

  /**
   * Route to appropriate sync method based on entity type
   *
   * `user` is optional because `resyncEntity` routes only the
   * `UserIndependentSyncEntityType` members, whose sync paths never read it.
   */
  private async syncEntityType(type: SyncEntityType, user?: UserEntity): Promise<void> {
    // console.log(`[syncSecureStorageUseCase] Syncing ${type}`);

    try {
      switch (type) {
        case SyncEntityType.LIKES:
          await this.syncLikes();
          break;
        case SyncEntityType.COMMENT_LIKES:
          await this.syncCommentLikes();
          break;
        case SyncEntityType.COMMUNITY_MESSAGES_LIKES:
          await this.syncCommunityMessageLikes();
          break;
        case SyncEntityType.SAVED_CONTENT:
          await this.syncSavedContent();
          break;
        case SyncEntityType.SAVED_USERS:
          await this.syncSavedUsers();
          break;
        case SyncEntityType.BLOCKED_USERS:
          if (this.hasUser(user, type)) {
            await this.syncBlockedUsers(user);
          }
          break;
        case SyncEntityType.MUTED_USERS:
          if (this.hasUser(user, type)) {
            await this.syncMutedUsers(user);
          }
          break;
        case SyncEntityType.USERS_BLOCKED_ME:
          if (this.hasUser(user, type)) {
            await this.syncUsersBlockedMe(user);
          }
          break;
        case SyncEntityType.BLOCKED_CONVERSATIONS:
          if (this.hasUser(user, type)) {
            await this.syncBlockedConversations(user);
          }
          break;
        case SyncEntityType.WALLET:
          await this.syncWallet();
          break;
        case SyncEntityType.EXCLUSIVE_CONTENT_SUBSCRIPTIONS:
          await this.syncExclusiveContentSubscriptions();
          break;
        case SyncEntityType.USER_SETTINGS:
          await this.syncUserSettings();
          break;
        case SyncEntityType.USER_PREMIUM_PACKAGES:
          await this.syncUserPremiumPackages();
          break;
        case SyncEntityType.PREMIUM_PACKAGES:
          await this.syncPremiumPackages();
          break;
        case SyncEntityType.GIFTS:
          await this.syncGifts();
          break;
        case SyncEntityType.RECHARGE_WALLET_PRODUCTS:
          await this.syncRechargeWalletProducts();
          break;
        case SyncEntityType.ROADMAP_LIKES:
          await this.syncRoadmapLikes();
          break;
        case SyncEntityType.ROADMAP_COMMENT_LIKES:
          await this.syncRoadmapCommentLikes();
          break;
        case SyncEntityType.DONATION_PRODUCTS:
          await this.syncDonationProducts();
          break;
        case SyncEntityType.UNLOCKED_CONTENT:
          await this.syncUnlockedContent();
          break;
        case SyncEntityType.DEVICE_ID:
          // TODO: Implement device ID sync for the logout 
          // functionality (logout user from other devices).
          // TODO: Implement Fireabase Push Tokens sync (check what's possible for the Web).
          break;
        case SyncEntityType.UPLOAD_PROFILE_IMAGE:
          if (this.hasUser(user, type)) {
            await this.syncUploadProfileImage(user);
          }
          break;
        case SyncEntityType.LOCAL_E2EE:
          await this.syncE2EE();
          break;
        default:
          console.warn(`[syncSecureStorageUseCase] Unhandled sync type: ${type}`);
      }

      await this.setEntitySynced(type);
      // console.log(`[syncSecureStorageUseCase] Successfully synced ${type}`);
    } catch (error) {
      appLogger.error(`Failed to sync ${type}`, 'SyncSecureStorageUseCase.syncEntityType', { error: error as Error });
      throw error;
    }
  }

  /**
   * Sync user's liked content (paginated)
   */
  private async syncLikes(): Promise<void> {
    let hasMore = true;
    let lastFetched: any = undefined;

    while (hasMore) {
      const response = await this.syncService.syncContentLikes(lastFetched);
      // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${response.items.length} likes`);

      for (const dto of response.items) {
        const storage = UserMediaLikeRemapper.toStorage(dto);
        const key = SECURE_STORAGE_KEYS.LIKES + storage.path;
        await this.secureStorage.setEncrypted(key, storage);
      }

      hasMore = response.hasMore;
      lastFetched = response.cursor;
    }
  }

  /**
   * Sync comment likes (paginated)
   */
  private async syncCommentLikes(): Promise<void> {
    let hasMore = true;
    let lastFetched: any = undefined;

    while (hasMore) {
      const response = await this.syncService.syncCommentLikes(lastFetched);
      // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${response.items.length} comment likes`);

      for (const dto of response.items) {
        const storage = CommentLikeRemapper.toStorage(dto);
        const key = SECURE_STORAGE_KEYS.COMMENT_LIKES + storage.commentId;
        await this.secureStorage.setEncrypted(key, storage);
      }

      hasMore = response.hasMore;
      lastFetched = response.cursor;
    }
  }

  /**
   * Sync community message likes (paginated)
   */
  private async syncCommunityMessageLikes(): Promise<void> {
    let hasMore = true;
    let lastFetched: any = undefined;

    while (hasMore) {
      const response = await this.syncService.syncCommunityMessageLikes(lastFetched);
      // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${response.items.length} community message likes`);

      for (const dto of response.items) {
        const storage = CommunityMessageLikeRemapper.toStorage(dto);
        const key = SECURE_STORAGE_KEYS.COMMUNITY_MESSAGE_LIKES + storage.communityMessageId;
        await this.secureStorage.setEncrypted(key, storage);
      }

      hasMore = response.hasMore;
      lastFetched = response.cursor;
    }
  }

  /**
   * Sync saved content (paginated)
   */
  private async syncSavedContent(): Promise<void> {
    let hasMore = true;
    let lastFetched: any = undefined;

    while (hasMore) {
      const response = await this.syncService.syncSavedContent(lastFetched);
      // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${response.items.length} saved content items`);

      for (const dto of response.items) {
        const storage = SavedContentRemapper.toStorage(dto);
        const key = SECURE_STORAGE_KEYS.SAVED_CONTENT + storage.path;
        await this.secureStorage.setEncrypted(key, storage);
      }

      hasMore = response.hasMore;
      lastFetched = response.cursor;
    }
  }

  /**
   * Sync saved users (paginated)
   */
  private async syncSavedUsers(): Promise<void> {
    let hasMore = true;
    let lastFetched: any = undefined;

    while (hasMore) {
      const response = await this.syncService.syncSavedUsers(lastFetched);
      // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${response.items.length} saved users`);

      for (const dto of response.items) {
        const storage = SavedUserRemapper.toStorage(dto);
        const key = SECURE_STORAGE_KEYS.SAVED_USERS + storage.firebaseId;
        await this.secureStorage.setEncrypted(key, storage);
      }

      hasMore = response.hasMore;
      lastFetched = response.cursor;
    }
  }

  /**
   * Sync blocked users (from user object)
   */
  private async syncBlockedUsers(user: UserEntity): Promise<void> {
    const blockedUsers = user.blockedUsers || [];

    for (const firebaseId of blockedUsers) {
      const storage = BlockedUserRemapper.fromFirebaseId(firebaseId);
      const key = SECURE_STORAGE_KEYS.BLOCKED_USERS + firebaseId;
      await this.secureStorage.setEncrypted(key, storage);
    }
  }

  /**
   * Sync muted users (from user object)
   */
  private async syncMutedUsers(user: UserEntity): Promise<void> {
    const mutedUsers = user.mutedUsers || [];

    for (const firebaseId of mutedUsers) {
      const storage = MutedUserRemapper.fromFirebaseId(firebaseId);
      const key = SECURE_STORAGE_KEYS.MUTED_USERS + firebaseId;
      await this.secureStorage.setEncrypted(key, storage);
    }
  }

  /**
   * Sync users who blocked me (from user object)
   */
  private async syncUsersBlockedMe(user: UserEntity): Promise<void> {
    const usersBlockedMe = user.usersBlockedMe || [];

    for (const firebaseId of usersBlockedMe) {
      const storage = UserBlockedMeRemapper.fromFirebaseId(firebaseId);
      const key = SECURE_STORAGE_KEYS.USERS_BLOCKED_ME + firebaseId;
      await this.secureStorage.setEncrypted(key, storage);
    }
  }

  /**
   * Sync blocked conversations (from user object)
   */
  private async syncBlockedConversations(user: UserEntity): Promise<void> {
    const blockedConversations = user.blockedConversations || [];

    for (const firebaseId of blockedConversations) {
      const storage = BlockedConversationRemapper.fromFirebaseId(firebaseId);
      const key = SECURE_STORAGE_KEYS.BLOCKED_CONVERSATIONS + firebaseId;
      await this.secureStorage.setEncrypted(key, storage);
    }
  }

  /**
   * Sync wallet data
   */
  private async syncWallet(): Promise<void> {
    const dto = await this.syncService.getWallet();

    if (dto) {
      const storage = WalletRemapper.toStorage(dto);
      // console.log(`[syncSecureStorageUseCase]------------------- Fetched wallet data. Coins: 
      //   ${storage.coins}; diamonds: ${storage.diamonds}, currency: ${storage.currency}`);
      await this.secureStorage.setEncrypted(SECURE_STORAGE_KEYS.WALLET, storage);
    }
  }

  /**
   * Sync exclusive content subscriptions
   */
  private async syncExclusiveContentSubscriptions(): Promise<void> {
    const dtos = await this.syncService.getExclusiveContentSubscriptions();
    // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${dtos.length} exclusive content subscriptions`);

    for (const dto of dtos) {
      const storage = ExclusiveContentSubscriptionRemapper.toStorage(dto);
      const key = SECURE_STORAGE_KEYS.EXCLUSIVE_SUBSCRIPTIONS + storage.userId;
      await this.secureStorage.setEncrypted(key, storage);
    }
  }

  /**
   * Sync user settings
   * Handles removal of obsolete settings (for Firebase Remote Config updates)
   */
  private async syncUserSettings(): Promise<void> {
    const dtos = await this.syncService.getUserSettings();
    // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${dtos.length} user settings`);

    if (dtos.length === 0) {
      return;
    }

    // Get all existing user settings from local storage (more efficient than getAllKeysWithPrefix)
    const existingSettings = await this.secureStorage.getAllWithPrefix<any>(SECURE_STORAGE_KEYS.USER_SETTINGS);

    // Build set of new setting names from server
    const newSettingNames = new Set(dtos.map(dto => dto.name));

    // Find and remove settings that exist locally but not on server anymore
    // This handles Firebase Remote Config updates that remove settings
    for (const existingSetting of existingSettings) {
      if (!newSettingNames.has(existingSetting.name)) {
        // console.log(`[syncSecureStorageUseCase] Removing obsolete setting: ${existingSetting.name}`);
        const key = SECURE_STORAGE_KEYS.USER_SETTINGS + existingSetting.name;
        await this.secureStorage.remove(key);
      }
    }

    // Store/update all settings from server
    for (const dto of dtos) {
      const storage = UserSettingRemapper.toStorage(dto);
      const key = SECURE_STORAGE_KEYS.USER_SETTINGS + storage.name;
      await this.secureStorage.setEncrypted(key, storage);
    }
  }

  /**
   * Sync user's premium packages
   */
  private async syncUserPremiumPackages(): Promise<void> {
    const dtos = await this.syncService.getUserPremiumPackages();
    // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${dtos.length} user premium packages`);

    for (const dto of dtos) {
      const storage = UserPremiumPackageRemapper.toStorage(dto);

      if (storage.active) {
        const key = SECURE_STORAGE_KEYS.USER_PREMIUM_PACKAGES + storage.productId;
        await this.secureStorage.setEncrypted(key, storage);
      } else {
        // Remove inactive packages
        const key = SECURE_STORAGE_KEYS.USER_PREMIUM_PACKAGES + storage.productId;
        await this.secureStorage.remove(key);
      }
    }
  }

  /**
   * Sync available premium packages (app-level)
   */
  private async syncPremiumPackages(): Promise<void> {
    const dtos = await this.syncService.getPremiumPackages();
    // console.log(`[syncSecureStorageUseCase] Fetched ${dtos.length} Premium Packages:`, dtos.map(dto => ({ productId: dto.product_id, name: dto.name })));

    // Similar to syncUserSettings that cross checks existing with incoming items, 
    // here just clear all existing packages before syncing new ones
    // On Flutter AppStore / PlayStore are used to check purchase products existence 
    // and their prices / currency. Here we use Firestore as source of truth.
    // Flutter implementation cross checks too, but this is simpler
    const existingPackages = await this.secureStorage.getAllWithPrefix<any>(SECURE_STORAGE_KEYS.PREMIUM_PACKAGES);

    for (const existingPackage of existingPackages) {
      const key = SECURE_STORAGE_KEYS.PREMIUM_PACKAGES + existingPackage.productId;
      await this.secureStorage.remove(key);
    }

    // Store all fetched premium packages
    for (const dto of dtos) {
      const storage = PremiumPackageRemapper.toStorage(dto);
      const key = SECURE_STORAGE_KEYS.PREMIUM_PACKAGES + storage.productId;
      await this.secureStorage.setEncrypted(key, storage);
    }
  }

  /**
   * Sync available gifts (app-level)
   */
  private async syncGifts(): Promise<void> {
    const dtos = await this.syncService.getGifts();
    // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${dtos.length} gifts`);

    // Just like premium packages, clear existing gifts before syncing new ones
    // On Flutter AppStore / PlayStore are used to check purchase products existence 
    // and their prices / currency. Here we use Firestore as source of truth.
    // Flutter implementation cross checks too, but this is simpler
    const existingGifts = await this.secureStorage.getAllWithPrefix<any>(SECURE_STORAGE_KEYS.GIFTS);

    for (const existingGift of existingGifts) {
      const key = SECURE_STORAGE_KEYS.GIFTS + existingGift.id;
      await this.secureStorage.remove(key);
    }

    // Store all fetched gifts
    for (const dto of dtos) {
      const storage = GiftRemapper.toStorage(dto);
      const key = SECURE_STORAGE_KEYS.GIFTS + storage.id;
      await this.secureStorage.setEncrypted(key, storage);
    }
  }

  /**
   * Sync recharge wallet products (app-level)
   */
  private async syncRechargeWalletProducts(): Promise<void> {
    const dtos = await this.syncService.getRechargeWalletProducts();
    // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${dtos.length} recharge wallet products`);

    // On Flutter AppStore / PlayStore are used to check purchase products existence 
    // and their prices / currency. Here we use Firestore as source of truth.
    // Just like premium packages, clear existing products before syncing new ones
    // Flutter implementation cross checks too, but this is simpler
    const existingProducts = await this.secureStorage.getAllWithPrefix<any>(SECURE_STORAGE_KEYS.RECHARGE_PRODUCTS);

    for (const existingProduct of existingProducts) {
      const key = SECURE_STORAGE_KEYS.RECHARGE_PRODUCTS + existingProduct.productId;
      await this.secureStorage.remove(key);
    }

    // Store all fetched recharge wallet products
    for (const dto of dtos) {
      const storage = RechargeWalletProductRemapper.toStorage(dto);
      const key = SECURE_STORAGE_KEYS.RECHARGE_PRODUCTS + storage.productId;
      await this.secureStorage.setEncrypted(key, storage);
    }
  }

  /**
   * Sync donation products (app-level)
   */
  private async syncDonationProducts(): Promise<void> {
    const dtos = await this.syncService.getDonationProducts();
    // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${dtos.length} donation products`);

    // On Flutter AppStore / PlayStore are used to check purchase products existence 
    // and their prices / currency. Here we use Firestore as source of truth.
    // Just like premium packages, clear existing products before syncing new ones
    // Flutter implementation cross checks too, but this is simpler

    const existingProducts = await this.secureStorage.getAllWithPrefix<any>(SECURE_STORAGE_KEYS.DONATION_PRODUCTS);

    for (const existingProduct of existingProducts) {
      const key = SECURE_STORAGE_KEYS.DONATION_PRODUCTS + existingProduct.productId;
      await this.secureStorage.remove(key);
    }

    for (const dto of dtos) {
      const storage = DonationProductRemapper.toStorage(dto);
      const key = SECURE_STORAGE_KEYS.DONATION_PRODUCTS + storage.productId;
      await this.secureStorage.setEncrypted(key, storage);
    }
  }

  /**
   * Sync roadmap likes (paginated)
   */
  private async syncRoadmapLikes(): Promise<void> {
    let hasMore = true;
    let lastFetched: any = undefined;

    while (hasMore) {
      const response = await this.syncService.syncRoadmapLikes(lastFetched);
      // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${response.items.length} roadmap likes`);

      for (const dto of response.items) {
        const storage = RoadmapItemLikeRemapper.toStorage(dto);
        const key = SECURE_STORAGE_KEYS.ROADMAP_LIKES + storage.roadmapItemId;
        await this.secureStorage.setEncrypted(key, storage);
      }

      hasMore = response.hasMore;
      lastFetched = response.cursor;
    }
  }

  /**
   * Sync roadmap comment likes (paginated)
   */
  private async syncRoadmapCommentLikes(): Promise<void> {
    let hasMore = true;
    let lastFetched: any = undefined;

    while (hasMore) {
      const response = await this.syncService.syncRoadmapCommentLikes(lastFetched);
      // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${response.items.length} roadmap comment likes`);

      for (const dto of response.items) {
        const storage = RoadmapCommentLikeRemapper.toStorage(dto);
        const key = SECURE_STORAGE_KEYS.ROADMAP_COMMENT_LIKES + storage.commentId;
        await this.secureStorage.setEncrypted(key, storage);
      }

      hasMore = response.hasMore;
      lastFetched = response.cursor;
    }
  }

  /**
   * Sync unlocked content (paginated)
   */
  private async syncUnlockedContent(): Promise<void> {
    let hasMore = true;
    let lastFetched: any = undefined;

    while (hasMore) {
      const response = await this.syncService.syncUnlockedContent(lastFetched);
      // console.log(`[syncSecureStorageUseCase]------------------- Fetched ${response.items.length} unlocked content items`);

      for (const dto of response.items) {
        const storage = UnlockedContentRemapper.toStorage(dto);
        const key = SECURE_STORAGE_KEYS.UNLOCKED_CONTENT + storage.path;
        await this.secureStorage.setEncrypted(key, storage);
      }

      hasMore = response.hasMore;
      lastFetched = response.cursor;
    }
  }

  /**
   * Upload user profile image to Firebase Storage
   * Matches Flutter's _uploadUserProfileImage from sync_repository.dart
   * 
   * Flow:
   * 1. Check if already uploaded (contains bucket name)
   * 2. Download remote image if HTTP/HTTPS (e.g., from Google Auth)
   * 3. Compress image (400px width)
   * 4. Upload to expause-profiles bucket
   * 5. Update Firestore with short path
   */
  private async syncUploadProfileImage(user: UserEntity): Promise<void> {
    // console.log('[syncSecureStorageUseCase] Checking profile image upload status');

    // Check if already uploaded (matching Flutter's profileImageUploaded getter)
    if (UserUtils.isProfileImageUploaded(user)) {
      // console.log('[syncSecureStorageUseCase] Profile image already uploaded, skipping');
      return;
    }

    // Upload profile image to Firebase Storage bucket
    const uploadedImagePath = await cloudStorageService.uploadUserProfileImage(
      user.profileImage
    );

    if (!uploadedImagePath) {
      console.warn('[syncSecureStorageUseCase] Failed to upload profile image');
      return;
    }

    // Update Firestore with new image path
    await userService.saveUserProfileImage(uploadedImagePath);

    // console.log('[syncSecureStorageUseCase] Profile image uploaded successfully:', uploadedImagePath);
  }

  /**
   * Sync E2EE encryption key (private JWK)
   * Fetches user's private JWK and stores it locally for encryption/decryption
   * Matches Flutter's saveLocalE2EE from users_repository.dart
   */
  private async syncE2EE(): Promise<void> {
    const privateJwk = await getUserPrivateJwkUseCase.execute();

    if (privateJwk) {
      await this.secureStorage.setEncrypted(SECURE_STORAGE_KEYS.PRIVATE_JWK, privateJwk);
      // console.log('[syncSecureStorageUseCase] Successfully stored private JWK');
    } else {
      console.warn('[syncSecureStorageUseCase] No private JWK returned from server');
    }
  }
}
